import { useState, type FormEvent } from 'react';
import { PASSWORD_MIN } from '@bs/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { api, rememberCsrf } from '../../api/instance.js';
import { ApiError, errorMessage } from '../../api/client.js';
import { AuthShell } from './AuthShell.js';
import { FormField } from '../../components/ui/form-field.js';
import { Input } from '../../components/ui/input.js';
import { Button } from '../../components/ui/button.js';
import { InlineAlert } from '../../components/ui/states.js';

export function LoginPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.call('login', { body: { email, password } });
      rememberCsrf(res.csrfToken);
      qc.setQueryData(['me'], res.user);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'VALIDATION_FAILED' && err.details
        ? err.details.map((d) => d.message).join('; ')
        : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
      <p className="mt-1 text-sm text-muted-foreground">Use your work email and password.</p>
      <form className="mt-8 space-y-4" onSubmit={submit}>
        <FormField help="f.auth.email" label="Email"><Input type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></FormField>
        <FormField help="f.auth.password" label="Password">
          <div className="relative">
            <Input type={show ? 'text' : 'password'} autoComplete="current-password" required minLength={PASSWORD_MIN}
              value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" aria-label="Password" />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </FormField>
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <Button type="submit" size="lg" className="w-full" loading={busy}>{busy ? 'Signing in…' : 'Sign in'}</Button>
      </form>
      <p className="mt-6 text-center text-xs text-muted-foreground">Forgot your password? Ask your admin for a new invite.</p>
    </AuthShell>
  );
}
